import loadable from "@loadable/component";
import { inject, observer } from "mobx-react";
import { Fragment, useCallback, useMemo, useState } from "react";
import { isAudienceAppRecord } from "../../lib/guards";
import { Grid, Heading, Modal } from "../../ui";
import { HeadingProps } from "../../ui/Heading.component";
import { DatasourceProps } from "./Datasource.component";
import { NoAudienceFeedItemMessage } from "./NoAudienceFeedItemMessage.component";
const AudienceFeedItem = loadable(() => import("./AudienceFeedItem.component"));
import { isFeedResult } from "@inferno/renderer-shared-core";

import styles from "./AudienceDatasource.module.scss";
export const AudienceDatasource = inject("store")(
  observer((props: DatasourceProps) => {
    const { store, title, content, feedId, displayHints, block } = props;

    if (!store) {
      return null;
    }

    if (!content?.length) {
      if (props.displayHints.hints?.includes("display-hints/message-for-no-results")) {
        return <NoAudienceFeedItemMessage />;
      }
      return null;
    }
    const [showModal, setShowModal] = useState(false);
    const [modalTerms, setModalTerms] = useState("false");

    const handleUserDismiss = useCallback(() => {
      setModalTerms("");
      setShowModal(false);
    }, [setShowModal, setModalTerms]);

    const handleUserClick = useCallback(
      (newTerms = "") => {
        if (!newTerms) {
          return;
        }

        setModalTerms(newTerms);
        setShowModal(true);
      },
      [setShowModal, setModalTerms],
    );

    const heading: HeadingProps = useMemo(() => props.heading ?? { level: 2 }, [props.heading]);
    const header = useMemo(() => (title ? <Heading {...heading}>{title}</Heading> : null), [title, heading]);

    const items = useMemo(
      () =>
        content.map((item, i) => {
          const { style } = displayHints;
          if (!isFeedResult(item) || !isAudienceAppRecord(item.record)) {
            return null;
          }

          return (
            <Fragment key={`feed-item-${feedId}-${(item as any).app_id}-${i}`}>
              <AudienceFeedItem item={item.record} style={style} callback={handleUserClick} />
            </Fragment>
          );
        }),
      [content, feedId, displayHints, handleUserClick],
    );

    return (
      <Fragment key={block.id}>
        {header}
        <div
          className={`feed-cards ${styles.componentAudienceDatasource}`}
          data-block-id={block.id}
          suppressHydrationWarning={true}
        >
          <Grid columns={displayHints.columns}>{items}</Grid>
        </div>
        <Modal open={showModal} callback={handleUserDismiss} addClass={styles.audienceModal}>
          <div className={styles.audienceTerms} dangerouslySetInnerHTML={{ __html: modalTerms }} />
        </Modal>
      </Fragment>
    );
  }),
);

export default AudienceDatasource;
